'use client';

import React, { useCallback, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  useSensor,
  useSensors,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  closestCenter,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, AlertTriangle, CheckCircle2, ArrowUpDown } from 'lucide-react';

type TaskStatus = 'todo' | 'in-progress' | 'done' | 'issue';

interface Task {
  id: string;
  title: string;
  projectId: string;
  date: string;
  status: TaskStatus;
  assignee?: string;
  assignees?: string;
  memo?: string;
  aiTags?: string[];
  [key: string]: any;
}

interface DndSchedulerProps {
  isDark: boolean;
  editMode: boolean;
  tasks: Task[];
  dateStr: string;
  getLegendColorClass: (title: string, projectName: string) => string;
  getProjectName: (projectId: string) => string;
  onTaskMove: (taskId: string, fromDate: string, toDate: string) => void;
  onTaskReorder: (dateStr: string, reorderedTaskIds: string[]) => void;
  onTaskClick: (task: Task) => void;
  onTaskDoubleClick: (task: Task) => void;
  onTaskStatusToggle: (taskId: string) => void;
  onTaskDelete: (taskId: string) => void;
  onTaskContextMenu?: (e: React.MouseEvent, task: Task) => void;
  onTaskAssigneeManage?: (e: React.MouseEvent, task: Task) => void;
  onTaskSubtaskAdd?: (e: React.MouseEvent, task: Task) => void;
}

// ── 드래그 가능 태스크 아이템 ──
function SortableTask({
  task,
  isDark,
  editMode,
  getLegendColorClass,
  projectName,
  onTaskClick,
  onTaskDoubleClick,
  onTaskStatusToggle,
  onTaskDelete,
  onTaskContextMenu,
  onTaskAssigneeManage,
  onTaskSubtaskAdd,
  onEditModeToggle,
  isEditing,
  editValue,
  onEditValueChange,
  onEditSubmit,
  onEditCancel,
}: {
  task: Task;
  isDark: boolean;
  editMode: boolean;
  getLegendColorClass: (title: string, projectName: string) => string;
  projectName: string;
  onTaskClick: (task: Task) => void;
  onTaskDoubleClick: (task: Task) => void;
  onTaskStatusToggle: (taskId: string) => void;
  onTaskDelete: (taskId: string) => void;
  onTaskContextMenu?: (e: React.MouseEvent, task: Task) => void;
  onTaskAssigneeManage?: (e: React.MouseEvent, task: Task) => void;
  onTaskSubtaskAdd?: (e: React.MouseEvent, task: Task) => void;
  onEditModeToggle?: () => void;
  isEditing?: boolean;
  editValue?: string;
  onEditValueChange?: (val: string) => void;
  onEditSubmit?: (taskId: string) => void;
  onEditCancel?: () => void;
}) {
  // ★ 미완료 과제만 드래그 허용 — done 만 차단 (issue 는 재스케줄 필요할 수 있어 허용)
  // editMode 일 때만 활성화, task.locked 플래그 존중
  const isDraggableTask = editMode && task.status !== 'done' && !task.locked;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id, disabled: !isDraggableTask });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };

  const displayTitle = task.title.replace(/^[■*\s]+/, '');

  return (
    <div
      ref={setNodeRef}
      style={style as any}
      className={`group relative ${isDragging ? 'ring-2 ring-indigo-500/50 rounded-lg' : ''}`}
    >
      <div
        className={`flex items-start text-[11.5px] leading-[1.3] p-1 -m-1 rounded transition-colors ${
          editMode
            ? 'cursor-grab active:cursor-grabbing hover:bg-amber-500/10 border border-transparent hover:border-amber-500/30'
            : 'cursor-pointer hover:bg-gray-500/10'
        }`}
        onClick={editMode ? undefined : () => onTaskClick(task)}
        onDoubleClick={editMode ? () => onTaskDoubleClick(task) : undefined}
        onContextMenu={(e) => {
          if (onTaskContextMenu) {
            e.preventDefault();
            e.stopPropagation();
            onTaskContextMenu(e, task);
          }
        }}
        title={task.assignees ? `담당자: ${task.assignees}\n${task.title}` : task.title}
      >
        {isDraggableTask ? (
          <div {...attributes} {...listeners} className="touch-none" title="드래그하여 날짜/우선순위 변경">
            <ArrowUpDown className={`w-3 h-3 mt-[1px] mr-1 flex-shrink-0 ${editMode ? 'text-amber-500' : 'text-gray-400 opacity-0 group-hover:opacity-100'} transition-opacity cursor-grab active:cursor-grabbing hover:text-indigo-500`} />
          </div>
        ) : (
          // 완료/이슈 태스크는 핸들 숨김 (여백 유지로 레이아웃 shift 방지)
          <div className="w-3 mr-1 flex-shrink-0" aria-hidden="true" />
        )}
        <span 
          className={`w-2.5 h-2.5 mt-[2.5px] mr-1.5 flex-shrink-0 cursor-pointer hover:scale-125 hover:ring-2 hover:ring-offset-1 hover:ring-indigo-400 transition-all ${getLegendColorClass(task.title, projectName)}`}
          onClick={(e) => { e.stopPropagation(); onTaskStatusToggle(task.id); }}
          title="클릭하여 완료 상태 토글"
        ></span>
        <div className="flex-1 flex items-center flex-wrap">
          {isEditing ? (
            <input
              autoFocus
              value={editValue || ''}
              onChange={(e) => onEditValueChange?.(e.target.value)}
              onBlur={() => onEditSubmit?.(task.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onEditSubmit?.(task.id);
                if (e.key === 'Escape') onEditCancel?.();
              }}
              className={`w-full min-w-[50px] bg-transparent border-b-2 outline-none py-0.5 text-[11px] font-bold ${isDark ? 'border-amber-500 text-white' : 'border-amber-500 text-black'}`}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <span
              className={`truncate font-medium ${displayTitle.includes('휴일') ? 'text-red-500 font-bold' : ''} ${task.status === 'done' ? 'line-through opacity-40' : ''}`}
              onClick={editMode ? (e) => { e.stopPropagation(); onTaskStatusToggle(task.id); } : undefined}
            >
              {displayTitle}
            </span>
          )}
          {task.status === 'issue' && <AlertTriangle className="w-3 h-3 ml-1 text-rose-500 flex-shrink-0 animate-pulse" />}
          {task.status === 'done' && <CheckCircle2 className="w-3 h-3 ml-1 text-emerald-500 flex-shrink-0" />}
          
          {task.assignees && (
            <div className="flex flex-wrap gap-1 mt-1">
              {(Array.isArray(task.assignees) ? task.assignees.join(',') : String(task.assignees || '')).split(',').map((u: string, i: number) => u.trim() ? (
                <span key={i} className={`px-1 py-[1px] rounded text-[9px] font-bold ${isDark ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-700'}`}>
                  {u.trim()}
                </span>
              ) : null)}
            </div>
          )}
          {!task.assignees && editMode && (
            <span className={`px-1 py-[1px] rounded text-[9px] font-bold mt-1 inline-block ${isDark ? 'bg-gray-700 text-gray-500' : 'bg-gray-200 text-gray-400'}`}>
              미배정
            </span>
          )}
          {task.memo && (
            <div className="hidden group-hover:block absolute left-0 bottom-full mb-1 w-48 p-2 rounded-lg text-xs shadow-lg bg-gray-900 text-gray-100 z-[100] whitespace-pre-wrap border border-gray-700">
              {String(task.memo)}
            </div>
          )}
        </div>
        <div className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity pr-1 bg-gradient-to-l from-current via-current to-transparent bg-opacity-90 pl-4">
          {onTaskAssigneeManage && (
            <button
              onClick={(e) => { e.stopPropagation(); onTaskAssigneeManage(e, task); }}
              className="text-indigo-400 hover:text-indigo-500 rounded bg-white shadow-sm p-0.5 border border-indigo-100 dark:bg-gray-800 dark:border-gray-700"
              title="담당자 설정"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/><line x1="19" x2="19" y1="8" y2="14"/><line x1="22" x2="16" y1="11" y2="11"/></svg>
            </button>
          )}
          {!editMode && onEditModeToggle && (
            <button
              onClick={(e) => { e.stopPropagation(); onEditModeToggle(); }}
              className="text-amber-500 hover:text-amber-400 rounded bg-white shadow-sm p-0.5 border border-amber-200 dark:bg-gray-800 dark:border-gray-700"
              title="편집 모드 진입"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg>
            </button>
          )}
          {onTaskSubtaskAdd && (
            <button
              onClick={(e) => { e.stopPropagation(); onTaskSubtaskAdd(e, task); }}
              className="text-emerald-400 hover:text-emerald-500 rounded bg-white shadow-sm p-0.5 border border-emerald-100 dark:bg-gray-800 dark:border-gray-700"
              title="하위 업무 추가 (요청)"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" x2="12" y1="5" y2="19"/><line x1="5" x2="19" y1="12" y2="12"/></svg>
            </button>
          )}
          {editMode && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); onTaskDoubleClick(task); }}
                className="text-amber-500 hover:text-amber-400 rounded bg-white shadow-sm p-0.5 border border-amber-200 dark:bg-gray-800 dark:border-gray-700"
                title="제목 수정"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onTaskDelete(task.id); }}
                className="text-rose-500 hover:text-rose-400 rounded bg-white shadow-sm p-0.5 border border-rose-100 dark:bg-gray-800 dark:border-gray-700"
                title="삭제"
              >
                ✕
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── 드래그 오버레이 (드래그 중 떠다니는 아이템) ──
function DragOverlayItem({ task, isDark }: { task: Task; isDark: boolean }) {
  const displayTitle = task.title.replace(/^[■*\s]+/, '');
  return (
    <div className={`px-3 py-2 rounded-xl shadow-2xl border-2 border-indigo-500/50 backdrop-blur-sm max-w-[250px] ${
      isDark ? 'bg-gray-800/95 text-white' : 'bg-white/95 text-gray-900'
    }`}>
      <div className="flex items-center gap-2 text-xs font-medium">
        <ArrowUpDown className="w-3 h-3 text-indigo-400" />
        <span className="truncate">{displayTitle}</span>
      </div>
    </div>
  );
}

// ── 드롭 가능 날짜 컬럼 ──
export function DroppableDayColumn({
  isDark,
  editMode,
  dateStr,
  isToday,
  tasks,
  getLegendColorClass,
  getProjectName,
  onTaskClick,
  onTaskDoubleClick,
  onTaskStatusToggle,
  onTaskDelete,
  onTaskContextMenu,
  onTaskAssigneeManage,
  onTaskSubtaskAdd,
  onEditModeToggle,
  editingTaskId,
  editValue,
  onEditValueChange,
  onEditSubmit,
  onEditCancel,
  children,
}: {
  isDark: boolean;
  editMode: boolean;
  dateStr: string;
  isToday: boolean;
  tasks: Task[];
  getLegendColorClass: (title: string, projectName: string) => string;
  getProjectName: (projectId: string) => string;
  onTaskClick: (task: Task) => void;
  onTaskDoubleClick: (task: Task) => void;
  onTaskStatusToggle: (taskId: string) => void;
  onTaskDelete: (taskId: string) => void;
  onTaskContextMenu?: (e: React.MouseEvent, task: Task) => void;
  onTaskAssigneeManage?: (e: React.MouseEvent, task: Task) => void;
  onTaskSubtaskAdd?: (e: React.MouseEvent, task: Task) => void;
  onEditModeToggle?: () => void;
  editingTaskId?: string | null;
  editValue?: string;
  onEditValueChange?: (val: string) => void;
  onEditSubmit?: (taskId: string) => void;
  onEditCancel?: () => void;
  children?: React.ReactNode;
}) {
  const { setNodeRef } = useDroppable({ id: dateStr });

  return (
    <div ref={setNodeRef} className="flex-1 min-h-[70px] flex flex-col">
      <SortableContext items={tasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
        <div className="flex flex-col gap-1.5 flex-1 h-full">
          {tasks.map((t) => (
          <SortableTask
            key={t.id}
            task={t}
            isDark={isDark}
            editMode={editMode}
            getLegendColorClass={getLegendColorClass}
            projectName={getProjectName(t.projectId)}
            onTaskClick={onTaskClick}
            onTaskDoubleClick={onTaskDoubleClick}
            onTaskStatusToggle={onTaskStatusToggle}
            onTaskDelete={onTaskDelete}
            onTaskContextMenu={onTaskContextMenu}
            onTaskAssigneeManage={onTaskAssigneeManage}
            onTaskSubtaskAdd={onTaskSubtaskAdd}
            onEditModeToggle={onEditModeToggle}
            isEditing={editingTaskId === t.id}
            editValue={editValue}
            onEditValueChange={onEditValueChange}
            onEditSubmit={onEditSubmit}
            onEditCancel={onEditCancel}
          />
        ))}
        {children}
        </div>
      </SortableContext>
    </div>
  );
}

// ── 메인 DnD 프로바이더 ──
export function DndSchedulerProvider({
  children,
  isDark,
  editMode,
  tasks,
  onTaskMove,
  onReorder,
}: {
  children: React.ReactNode;
  isDark: boolean;
  editMode: boolean;
  tasks: Task[];
  onTaskMove: (taskId: string, fromDate: string, toDate: string) => void;
  onReorder: (allTasks: Task[], dateReorderedTasks?: Task[]) => void;
}) {
  const [activeTask, setActiveTask] = useState<Task | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    }),
    useSensor(KeyboardSensor)
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const task = tasks.find(t => t.id === event.active.id);
    setActiveTask(task || null);
  }, [tasks]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    setActiveTask(null);

    if (!over || active.id === over.id) return;

    const activeTaskData = tasks.find(t => t.id === active.id);
    const overTaskData = tasks.find(t => t.id === over.id);

    if (!activeTaskData) return;

    // ★ 클라이언트 이중 방어: disabled 걸려있지만 혹시 모를 우회 방지
    if (activeTaskData.status === 'done' || activeTaskData.locked) {
      if (typeof window !== 'undefined') {
        console.warn('[Scheduler] 완료/잠긴 태스크는 이동 불가:', activeTaskData.title);
      }
      return;
    }

    let targetDate = '';
    if (overTaskData) {
      targetDate = overTaskData.date;
    } else if (typeof over.id === 'string' && over.id.match(/^\d{4}-\d{2}-\d{2}$/)) {
      targetDate = over.id;
    }

    if (!targetDate) return;

    // 같은 날짜 내 재정렬
    if (activeTaskData.date === targetDate) {
      if (overTaskData) {
        const dateTasks = tasks.filter(t => t.date === activeTaskData.date);
        const oldIndex = dateTasks.findIndex(t => t.id === active.id);
        const newIndex = dateTasks.findIndex(t => t.id === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
          const reordered = arrayMove(dateTasks, oldIndex, newIndex).map((t: Task, idx: number) => ({ ...t, sortOrder: idx }));
          const otherTasks = tasks.filter(t => t.date !== activeTaskData.date);
          onReorder([...otherTasks, ...reordered], reordered);
        }
      }
      return;
    }

    // 다른 날짜로 이동 — 과거 날짜 이동 시 경고
    const today = new Date().toISOString().slice(0, 10);
    if (targetDate < today && typeof window !== 'undefined') {
      const ok = window.confirm(
        `"${activeTaskData.title.replace(/^[■*\s]+/, '')}" 을(를) 과거 날짜(${targetDate})로 이동합니다.\n계속하시겠습니까?`
      );
      if (!ok) return;
    }

    onTaskMove(activeTaskData.id, activeTaskData.date, targetDate);
  }, [tasks, onTaskMove, onReorder]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      {children}
      <DragOverlay dropAnimation={{
        duration: 200,
        easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
      }}>
        {activeTask && <DragOverlayItem task={activeTask} isDark={isDark} />}
      </DragOverlay>
    </DndContext>
  );
}

export default DndSchedulerProvider;
